/**
 * Sheet/Slider para gerenciamento de Status do WhatsApp
 * 
 * Abre da ESQUERDA para a DIREITA listando todas as inboxes
 * WhatsApp com indicadores de status ativo
 * 
 * @version 1.0.0
 * @release 2025-02-08
 */

import { useState, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Plus, ArrowLeft, Loader2 } from 'lucide-react';
import { WhatsAppStatusIcon } from '@/components/icons/WhatsAppStatusIcon';
import { StatusInboxCard } from './StatusInboxCard';
import { StatusPostsList } from './StatusPostsList';
import { CreateStatusDialog } from './CreateStatusDialog';
import { useWhatsAppStatus, useStatusCountByInbox, isStatusSupported } from '@/hooks/useWhatsAppStatus';
import { useInboxes, type InboxData } from '@/hooks/useInboxes';

interface WhatsAppStatusSheetProps {
  /** Se o sheet está aberto */
  open: boolean;
  /** Callback ao fechar */
  onOpenChange: (open: boolean) => void;
  /** ID do tenant para buscar inboxes */
  tenantId?: string;
}

/**
 * Sheet lateral para gerenciamento de Status do WhatsApp
 * Busca inboxes internamente usando o hook useInboxes
 */
export function WhatsAppStatusSheet({
  open,
  onOpenChange,
  tenantId,
}: WhatsAppStatusSheetProps) {
  // Busca inboxes completas do tenant
  const { data: inboxes = [], isLoading: isLoadingInboxes } = useInboxes(tenantId);
  
  // Estado da inbox selecionada (para ver detalhes)
  const [selectedInbox, setSelectedInbox] = useState<InboxData | null>(null);
  
  // Estado do dialog de criação
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogInbox, setCreateDialogInbox] = useState<InboxData | null>(null);

  // Filtra apenas inboxes WhatsApp que suportam status
  const whatsappInboxes = useMemo(() => {
    return inboxes.filter(inbox => 
      inbox.channel_type === 'whatsapp' && 
      isStatusSupported(inbox.provider?.slug)
    );
  }, [inboxes]);

  // IDs das inboxes para buscar contagem
  const inboxIds = useMemo(() => 
    whatsappInboxes.map(i => i.id), 
    [whatsappInboxes]
  );

  // Hook para contagem de status por inbox
  const { data: statusCounts = {} } = useStatusCountByInbox(inboxIds);

  // Hook para status da inbox selecionada
  const {
    statusPosts,
    isLoading: isLoadingStatus,
    isCreating,
    isDeleting,
    createStatus,
    deleteStatus,
    activeCount,
  } = useWhatsAppStatus(selectedInbox?.id);

  // Total de status ativos em todas as inboxes
  const totalActiveStatus = Object.values(statusCounts).reduce((a, b) => a + b, 0);

  // Handler para abrir dialog de criação
  const handleOpenCreateDialog = (inbox: InboxData) => {
    setCreateDialogInbox(inbox);
    setCreateDialogOpen(true);
  };

  // Handler para criar status
  const handleCreateStatus = async (params: Parameters<typeof createStatus>[0]) => {
    await createStatus(params);
    setCreateDialogOpen(false);
  };

  // Handler para voltar à lista de inboxes
  const handleBackToList = () => {
    setSelectedInbox(null);
  };

  // Reset ao fechar
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedInbox(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="left" className="w-full sm:max-w-md p-0">
          {/* Header */}
          <SheetHeader className="p-4 pb-2 border-b">
            <div className="flex items-center gap-3">
              {selectedInbox && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleBackToList}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              
              <div className="flex items-center gap-2">
                <WhatsAppStatusIcon 
                  hasActiveStatus={totalActiveStatus > 0} 
                  size={24}
                  className="text-primary"
                />
                <div>
                  <SheetTitle className="text-left">
                    {selectedInbox ? selectedInbox.name : 'Status do WhatsApp'}
                  </SheetTitle>
                  <SheetDescription className="text-left text-xs">
                    {selectedInbox 
                      ? `${activeCount} status ${activeCount === 1 ? 'ativo' : 'ativos'}`
                      : `${whatsappInboxes.length} ${whatsappInboxes.length === 1 ? 'inbox disponível' : 'inboxes disponíveis'}`
                    }
                  </SheetDescription>
                </div>
              </div>

              {totalActiveStatus > 0 && !selectedInbox && (
                <Badge variant="secondary" className="ml-auto">
                  {totalActiveStatus} {totalActiveStatus === 1 ? 'ativo' : 'ativos'}
                </Badge>
              )}
            </div>
          </SheetHeader>

          {/* Conteúdo */}
          <div className="flex-1 overflow-hidden">
            {/* Visão: Lista de Inboxes */}
            {!selectedInbox && (
              <ScrollArea className="h-[calc(100vh-120px)]">
                <div className="p-4 space-y-4">
                  {/* Botão criar (global) */}
                  {whatsappInboxes.length > 0 && (
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        // Abre para a primeira inbox disponível
                        if (whatsappInboxes[0]) {
                          handleOpenCreateDialog(whatsappInboxes[0]);
                        }
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      Criar Novo Status
                    </Button>
                  )}

                  <Separator />

                  {/* Label da seção */}
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Minhas Inboxes
                  </p>

                  {/* Lista de inboxes */}
                  {isLoadingInboxes ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : whatsappInboxes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <WhatsAppStatusIcon size={40} className="text-muted-foreground mb-3" />
                      <p className="text-sm font-medium">Nenhuma inbox compatível</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Conecte uma inbox WhatsApp para usar Status
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {whatsappInboxes.map((inbox) => (
                        <StatusInboxCard
                          key={inbox.id}
                          inbox={inbox}
                          activeStatusCount={statusCounts[inbox.id] || 0}
                          onClick={() => setSelectedInbox(inbox)}
                          onCreateStatus={() => handleOpenCreateDialog(inbox)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}

            {/* Visão: Detalhes da Inbox */}
            {selectedInbox && (
              <div className="p-4 space-y-4">
                {/* Botão criar status nesta inbox */}
                <Button
                  className="w-full gap-2"
                  onClick={() => handleOpenCreateDialog(selectedInbox)}
                >
                  <Plus className="h-4 w-4" />
                  Criar Status
                </Button>

                <Separator />

                {/* Lista de status */}
                <StatusPostsList
                  posts={statusPosts}
                  isLoading={isLoadingStatus}
                  isDeleting={isDeleting}
                  onDelete={deleteStatus}
                  providerSlug={selectedInbox.provider?.slug}
                />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Dialog de criação de status */}
      <CreateStatusDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        inbox={createDialogInbox}
        onCreateStatus={handleCreateStatus}
        isCreating={isCreating}
      />
    </>
  );
}
